import { supabase } from './supabase';
import { noteFailure, type FailureReason } from './failures';

const reasonOf = (status: number): FailureReason =>
  status === 401 ? 'signed-out' : status === 403 ? 'forbidden' : status === 409 ? 'conflict' : status === 400 || status === 422 ? 'invalid' : status >= 500 ? 'server' : 'rejected';

/**
 * POST to this app's own `/api` server with the operator's session. Server problems come back as a human
 * sentence (the problem+json `detail`), a dropped connection as a plain "connection failed" line. With `timeoutMs`
 * a call that takes longer is given up on and reads as a dropped connection. Every failure is noted for Report.
 */
export async function postApi<T>(path: string, body: unknown, opts?: { timeoutMs?: number }): Promise<{ ok: boolean; data: T | null; message: string }> {
  const { data } = await supabase.auth.getSession();
  const ctrl = opts?.timeoutMs ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), opts!.timeoutMs) : null;
  try {
    const r = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session?.access_token ?? ''}` }, body: JSON.stringify(body), signal: ctrl?.signal });
    const j = (await r.json().catch(() => null)) as (T & { title?: string; detail?: string; trace_id?: string }) | null;
    if (r.ok) return { ok: true, data: j, message: '' };
    const message = j?.detail ?? j?.title ?? 'The server refused that request.';
    // noted so the screen that shows this sentence can offer Report for it
    noteFailure({ reason: reasonOf(r.status), message, apiPath: path, apiStatus: r.status, traceId: typeof j?.trace_id === 'string' ? j.trace_id : undefined });
    return { ok: false, data: null, message };
  } catch {
    const message = 'Connection failed — check your internet and try again.';
    noteFailure({ reason: 'network', message, apiPath: path });
    return { ok: false, data: null, message };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** GET a picture from this app's own `/api` server with the operator's session. Anything but a picture is `null`. */
export async function getApiBlob(path: string): Promise<Blob | null> {
  try {
    const { data } = await supabase.auth.getSession();
    const r = await fetch(path, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` }, cache: 'no-store' });
    if (!r.ok) return null;
    const blob = await r.blob();
    return blob.type.startsWith('image/') ? blob : null;
  } catch {
    return null;
  }
}
